import * as React from 'react';
import { Link } from '@tanstack/react-router';

import { useAuth, type AuthRole } from '@/auth/auth-context';

import { Sidebar } from './sidebar';
import { SupplierCguGate } from './supplier-cgu-gate';
import { Topbar } from './topbar';

// =============================================================================
// app-shell.tsx — Squelette d'application (design system : grille 240px | 1fr).
// =============================================================================
// DS layout : grille `240px | 1fr` ; sidebar sticky pleine hauteur ; topbar sticky 56px
// (blur) ; contenu paddé, capé à 1480px. Repères a11y : <nav> (Sidebar), <header> (Topbar),
// <main id="main">, lien d'évitement vers le contenu (WCAG 2.4.1). Tokens sémantiques uniquement.
// SEC-NAV-01 : ce chrome (Topbar + Sidebar role-gated) n'est monté QUE sous la layout route
// « authenticated » (router.tsx) — jamais sur les écrans pré-auth (landing, onboarding,
// activation, magic-link, signature CGU). Le gating est porté par le ROUTAGE, pas par un flag ici.

export interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps): React.JSX.Element {
  const year = new Date().getFullYear();
  // Le pied légal s'adresse au FOURNISSEUR : les CGU qu'il pointe sont celles qu'un
  // fournisseur signe, et leur chargement exige un contexte tenant. Pour un admin, le
  // lien rendait « Session fournisseur absente ou invalide : contexte tenant refusé » —
  // un cul-de-sac. On ne masque donc pas seulement le lien : le pied entier n'a pas
  // d'objet côté administration (relevé Gate Human du 2026-09-15).
  const auth = useAuth();
  const role: AuthRole = auth.role ?? 'admin';
  const montrerPiedLegal = role === 'supplier';
  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Aller au contenu principal
      </a>
      <div className="grid grid-cols-[240px_1fr]">
        <Sidebar />
        <div className="flex min-h-screen min-w-0 flex-col">
          <Topbar />
          <main id="main" className="flex-1 px-7 py-8">
            <div className="mx-auto w-full max-w-[1480px]">
              {/* Garde CGU (§3.1 l.2432-2434) : un fournisseur non-signataire est redirigé vers /cgu. */}
              <SupplierCguGate>{children}</SupplierCguGate>
            </div>
          </main>
          {/* Pied de page légal (DBU §3.2 l.2498) : accès légal + contact, sur les écrans
              authentifiés du FOURNISSEUR. Masqué pour l'admin — voir `montrerPiedLegal`. */}
          {montrerPiedLegal ? (
          <footer className="border-t border-border bg-card">
            <div className="mx-auto flex w-full max-w-[1480px] flex-wrap items-center justify-between gap-2 px-7 py-4 text-sm text-muted-foreground">
              <nav aria-label="Liens légaux et contact">
                <ul className="flex flex-wrap gap-x-4 gap-y-1">
                  <li>
                    {/* SPA Link (plus de rechargement complet) : la chrome reste montée (RootChrome
                        conserve l'AppShell sur ces pages légales pour un utilisateur authentifié). */}
                    <Link
                      to="/mentions-legales"
                      className="rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      Mentions légales
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/cgu"
                      className="rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      CGU
                    </Link>
                  </li>
                  <li>
                    <a
                      href="mailto:contact@jeko.biz"
                      className="rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      Contact JeKo
                    </a>
                  </li>
                </ul>
              </nav>
              <span>© {year} JEKO Référencement</span>
            </div>
          </footer>
          ) : null}
        </div>
      </div>
    </div>
  );
}
